import { useMutation, useQuery } from "@tanstack/react-query";

import { getPrivateInstance } from "../_privateInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

/**
 * Allowed purposes for POST /scheduled-tasks/trigger. There is no purposes
 * endpoint — the source of truth is `CASINO_SCHEDULED_TASK_PURPOSES` in
 * trivelta-admin-services
 * `services/casino/admin_panel_casino/casino/v1/scheduled_tasks/handler.py`;
 * keep this list in sync by hand.
 */
export const CASINO_SCHEDULED_TASK_PURPOSES = [
  "generate-betcore-access-token",
  "load-casino-games",
  "overwrite-casino-game-images",
  "overwrite-casino-game-images-force-refresh",
  "create-default-casino-entities",
];

const TERMINAL_STATUSES = new Set(["success", "failed", "orphaned"]);

/** @param {string | undefined} status */
export const isScheduledTaskTerminal = (status) =>
  TERMINAL_STATUSES.has(status);

export const SCHEDULED_TASK_POLL_INTERVAL_MS = 2500;
/** Client cap — stop polling a run that never reaches a terminal status. */
export const SCHEDULED_TASK_POLL_CAP_MS = 5 * 60 * 1000;

/**
 * Poll policy shared by the run-status and history-list polls — same idiom
 * as `useAsyncExport`: react-query owns the interval and a `false` return
 * stops it (terminal status, or the client cap). The cap is wall-clock time
 * since tracking started rather than a tick count: `dataUpdateCount` only
 * advances on success, so a run whose polls keep failing would otherwise
 * never reach it.
 * @param {number} startedAt `Date.now()` when tracking began
 * @param {string | undefined} status
 */
const pollIntervalFor = (startedAt, status) => {
  if (isScheduledTaskTerminal(status)) {
    return false;
  }
  if (Date.now() - startedAt >= SCHEDULED_TASK_POLL_CAP_MS) {
    return false;
  }
  return SCHEDULED_TASK_POLL_INTERVAL_MS;
};

/**
 * `refetchInterval` for the run-status poll.
 * @param {number} startedAt `Date.now()` when tracking began
 */
export const scheduledTaskPollInterval = (startedAt) => (query) =>
  pollIntervalFor(startedAt, query?.state?.data?.status);

/**
 * 403 from either scheduled-task read endpoint — the caller lacks
 * `casino.scheduled_tasks.read`, so neither poll can ever see the run.
 * @param {unknown} error
 */
export const isScheduledTaskForbidden = (error) =>
  error?.response?.status === 403;

/**
 * Tracked run looked up in a history-list payload. Fallback identity for
 * environments where GET by correlation_id 500s (correlation_id GSI not
 * provisioned yet) — the list scan is the only way to see the run finish.
 * @param {{ runs?: Array<{ correlation_id?: string | null }> } | undefined} data
 * @param {string | null | undefined} correlationId
 */
export const scheduledTaskRunFromList = (data, correlationId) =>
  data?.runs?.find((run) => run.correlation_id === correlationId) ?? null;

/**
 * `refetchInterval` for polling the history list instead of the single-run
 * endpoint (see `scheduledTaskRunFromList`). Same terminal-status / cap
 * rules as `scheduledTaskPollInterval`, applied to the list payload.
 * @param {string | null | undefined} correlationId
 * @param {number} startedAt `Date.now()` when tracking began
 */
export const scheduledTaskListPollInterval =
  (correlationId, startedAt) => (query) =>
    pollIntervalFor(
      startedAt,
      scheduledTaskRunFromList(query?.state?.data, correlationId)?.status,
    );

/**
 * Run history for one purpose, most recent first.
 * `refetchInterval` exists for the GSI-fallback poll above — react-query owns
 * the interval, so the caller can't just wrap the hook.
 * @param {string | undefined} purpose
 * @param {{ enabled?: boolean, limit?: number, refetchInterval?: import("@tanstack/react-query").UseQueryOptions["refetchInterval"] }} [options]
 */
export function useCasinoScheduledTaskRuns(purpose, options = {}) {
  const limit = options.limit ?? 20;
  return useQuery({
    queryKey: ["casino", "scheduledTasks", "runs", purpose, limit],
    queryFn: async () => {
      const { data } = await getPrivateInstance().get(
        API_ROUTES.CASINO_SCHEDULED_TASKS,
        { params: { purpose, limit } },
      );
      return data?.data ?? { runs: [], count: 0 };
    },
    enabled: options.enabled !== false && Boolean(purpose),
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: false,
  });
}

/** POST /scheduled-tasks/trigger — body `{ purpose }`, 202 with `correlation_id`. */
export function useTriggerCasinoScheduledTask() {
  return useMutation({
    mutationKey: ["casino", "scheduledTasks", "trigger"],
    mutationFn: async (/** @type {string} */ purpose) => {
      const { data } = await getPrivateInstance().post(
        API_ROUTES.CASINO_SCHEDULED_TASKS_TRIGGER,
        { purpose },
      );
      return data ?? null;
    },
  });
}

/**
 * One run by correlation id, polled until it reaches a terminal status or
 * `SCHEDULED_TASK_POLL_CAP_MS` after `startedAt`.
 * @param {string | null | undefined} correlationId
 * @param {number} startedAt `Date.now()` when tracking began
 */
export function useCasinoScheduledTaskRun(correlationId, startedAt) {
  return useQuery({
    queryKey: ["casino", "scheduledTasks", "run", correlationId],
    queryFn: async () => {
      const { data } = await getPrivateInstance().get(
        API_ROUTES.CASINO_SCHEDULED_TASK_RUN.replace(
          "{correlation_id}",
          // Backend-generated UUID today, but never trust it to be URL-safe.
          encodeURIComponent(correlationId),
        ),
      );
      return data?.data ?? null;
    },
    enabled: Boolean(correlationId),
    refetchInterval: scheduledTaskPollInterval(startedAt),
    refetchOnWindowFocus: false,
    retry: false,
  });
}
